import { useParams } from 'react-router-dom';

import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';

import ValidationMessage from 'components/validation/ValidationMessage';

import SharedTextFieldInput from 'components/fields/shared/TextFieldInput';
import ValidateFile from 'components/fields/shared/Message/components/ValidateFile';
import ValidateText from 'components/fields/shared/Message/components/ValidateText';

import useFormBinderDisabled from 'hooks/useFormBinderDisabled';

import { FILE_TYPE } from 'variables/constants';

import { useLapseResolver, CANCELLATION_REASONS } from './hooks';
import { getHTMLId, useResolverModal } from './resolverUtils';
import type { ValidationResolverProps } from './types';

export interface Validation034ResolverProps extends ValidationResolverProps {}

const VALIDATION_ID = '034';
const FORM_STATE_KEY = 'autoPersonalForm';

const Validation034Resolver = ({
  section,
  itemKey: propItemKey,
  compact = false,
}: Validation034ResolverProps) => {
  const { uuid } = useParams();
  const isBinderDisabled = useFormBinderDisabled(FORM_STATE_KEY);
  const {
    isOpen: isCompactOpen,
    onOpen: onCompactOpen,
    onClose: onCompactClose,
  } = useDisclosure();

  const itemKey = propItemKey || 'global';

  const { state, handlers, fieldIds, alertMessage, getCompactAlertStatus } =
    useLapseResolver({
      validationId: VALIDATION_ID,
      section,
      itemKey,
      formStateKey: FORM_STATE_KEY,
      loeFileType: FILE_TYPE.TYPE_034.type,
    });

  const htmlId = getHTMLId(VALIDATION_ID, itemKey);

  // Use shared modal hook for hash-based navigation
  const { handleModalClose } = useResolverModal(
    compact,
    Object.values(fieldIds),
    htmlId,
    onCompactOpen,
    onCompactClose,
  );

  if (!state.show) return null;

  // Compact mode: show button that opens modal
  if (compact) {
    return (
      <>
        {/* Anchor divs for mandatory field navigation */}
        <div id={htmlId}></div>
        <div id={fieldIds.cancellationReason}></div>
        <div id={fieldIds.otherExplanation}></div>
        <div id={fieldIds.noClaimsConfirmed}></div>
        <div id={fieldIds.remarks}></div>
        <Tooltip placement="top" label={alertMessage}>
          <Alert status={getCompactAlertStatus()} mt="10px" fontSize="14px">
            <Button
              onClick={() => onCompactOpen()}
              w="100%"
              whiteSpace="normal"
            >
              <AlertIcon />
              Add remark
            </Button>
          </Alert>
        </Tooltip>
        <Modal
          isOpen={isCompactOpen}
          onClose={handleModalClose}
          size="4xl"
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Lapse in Insurance History</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box mb="10px">
                <Validation034Resolver
                  section={section}
                  itemKey={itemKey}
                  compact={false}
                />
              </Box>
              <Button
                variant="brand"
                onClick={handleModalClose}
                mb="15px"
                float="right"
              >
                Close
              </Button>
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    );
  }

  return (
    <Box>
      {/* Anchor divs for mandatory field navigation */}
      <div id={htmlId}></div>
      <div id={fieldIds.cancellationReason}></div>
      <div id={fieldIds.otherExplanation}></div>
      <div id={fieldIds.noClaimsConfirmed}></div>
      <div id={fieldIds.remarks}></div>

      <Alert status="warning" mt="10px" fontSize="14px">
        <AlertIcon />
        <ValidationMessage message={alertMessage} />
      </Alert>

      <Box mt="15px" p="15px" bg="gray.50" borderRadius="md">
        {/* Cancellation Reason Question */}
        <FormControl mb="15px" id={fieldIds.cancellationReason} isRequired>
          <FormLabel fontWeight="semibold">
            Was the client cancelled by the insurer prior to the lapse for any
            of the following reasons?
          </FormLabel>
          <RadioGroup
            value={state.cancellationReason || ''}
            onChange={handlers.handleCancellationReasonChange}
            isDisabled={isBinderDisabled}
          >
            <Stack spacing={3}>
              {CANCELLATION_REASONS.map((reason) => (
                <Radio key={reason.value} value={reason.value}>
                  {reason.label}
                </Radio>
              ))}
            </Stack>
          </RadioGroup>
        </FormControl>

        {/* Other Explanation (shown when "other" is selected) */}
        {state.cancellationReason === 'other' && (
          <Box mb="15px" id={fieldIds.otherExplanation}>
            <SharedTextFieldInput
              label="Please explain the reason for the lapse"
              value={state.otherExplanation}
              onChange={handlers.handleOtherExplanationChange}
              borderColor={
                !state.otherExplanation ||
                state.otherExplanation.trim().length === 0
                  ? 'red'
                  : 'inherit'
              }
              formStateKey={FORM_STATE_KEY}
              section={section}
              required
            />
          </Box>
        )}

        {/* No Claims Confirmation Checkbox */}
        <FormControl mb="15px" id={fieldIds.noClaimsConfirmed} isRequired>
          <Alert status={getCompactAlertStatus()} mt="10px" fontSize="14px">
            <Checkbox
              isChecked={state.noClaimsConfirmed}
              onChange={handlers.handleNoClaimsConfirmedChange}
              size="md"
              isDisabled={isBinderDisabled}
            >
              <Text fontSize="sm" fontWeight="medium" color="gray.700">
                I have confirmed with the customer there were no claims of any
                kind during the lapse.
              </Text>
            </Checkbox>
          </Alert>
        </FormControl>

        {/* Remarks Field */}
        {state.remark && (
          <VStack spacing={4} align="stretch">
            <Box id={fieldIds.remarks}>
              <SharedTextFieldInput
                label="Remarks"
                value={state.remark.text || ''}
                onChange={handlers.handleRemarksChange}
                borderColor={
                  !state.remark.text || state.remark.text.trim().length === 0
                    ? 'red'
                    : 'inherit'
                }
                formStateKey={FORM_STATE_KEY}
                section={section}
                required
              />
            </Box>

            <ValidateText
              uuid={uuid}
              remark={state.remark}
              validationId={VALIDATION_ID}
              itemKey={itemKey}
              formStateKey={FORM_STATE_KEY}
              context={alertMessage}
            />

            {state.file && (
              <ValidateFile
                uuid={uuid}
                file={state.file}
                validationId={VALIDATION_ID}
                itemKey={itemKey}
                context={alertMessage}
                formStateKey={FORM_STATE_KEY}
              />
            )}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default Validation034Resolver;
